/**
 * v0.4 追加7要件② (D-234 §1-2(b)): 曜日種別の判定を1箇所に集約する (§8-55 の複製定義を作らない)。
 * 設計: docs/design/v04-shift-additional-requirements.md §1-2(b)
 *
 * `formatDateLabel` (lib/format.ts) の `WEEKDAY_LABELS` 相当をここへ集約し、`ShiftPrintPage.tsx` /
 * `ShiftStandingPatternsPage.tsx` が個別に持っていた同型の `DOW_LABELS` 定数もここから参照させる。
 *
 * 祝日は曜日に関わらず 'holiday' として扱う (土日に重なった場合も祝日表示を優先、日本の一般的な
 * カレンダー表記の慣例に合わせる)。holidayNames は 'YYYY-MM-DD' → 祝日名 の Map。
 */
export const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;

export type DayKind = 'weekday' | 'saturday' | 'sunday' | 'holiday';

const dateKey = (yyyyMmDd: string): string => yyyyMmDd.slice(0, 10);

/** タイムゾーン変換を避けるため、文字列の日付部分をそのまま暦日として解釈する (formatDateLabel と同じ方式)。 */
export const dayOfWeekOf = (yyyyMmDd: string): number => {
  const [y, m, d] = dateKey(yyyyMmDd).split('-').map(Number);
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).getUTCDay();
};

export const resolveDayKind = (
  yyyyMmDd: string,
  holidayNames?: ReadonlyMap<string, string> | null,
): DayKind => {
  if (holidayNames?.has(dateKey(yyyyMmDd))) return 'holiday';
  const dow = dayOfWeekOf(yyyyMmDd);
  if (dow === 0) return 'sunday';
  if (dow === 6) return 'saturday';
  return 'weekday';
};

export const holidayNameOf = (
  yyyyMmDd: string,
  holidayNames?: ReadonlyMap<string, string> | null,
): string | null => holidayNames?.get(dateKey(yyyyMmDd)) ?? null;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** タイムゾーン変換を避けるため文字列の日付部分をそのまま暦日として加算する (dayOfWeekOf と同じ方式)。
 *  v0.4 追加7要件③ (年間カレンダーの期間一括登録) で新設。既存の複数画面 (ShiftPrintPage.tsx 等) が
 *  個別に同型の実装を持つが、それらへの横断リファクタは別セッションのスコープとする (§8-55)。 */
export const addDays = (yyyyMmDd: string, days: number): string => {
  const [y, m, d] = dateKey(yyyyMmDd).split('-').map(Number);
  const dt = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + days));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
};

export const buildDateList = (fromYyyyMmDd: string, toYyyyMmDd: string): string[] => {
  const out: string[] = [];
  for (let cur = fromYyyyMmDd; cur <= toYyyyMmDd; cur = addDays(cur, 1)) out.push(cur);
  return out;
};

/** 曜日文字の色クラス。'weekday' (平日) は既定色のため空文字を返す。
 *  祝日は日曜と同じ赤系トークンを使う (日本の一般的なカレンダー表記の慣例)。 */
export const dayTextClass = (kind: DayKind): string => {
  if (kind === 'saturday') return 'text-calendar-saturday';
  if (kind === 'sunday' || kind === 'holiday') return 'text-calendar-sunday';
  return '';
};
